import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import {
  getMyVolunteerHistory,
  withdrawSignup,
  type VolunteerSignup,
} from "@/api/volunteers";

/**
 * Lab UI for the Volunteer system.
 *
 * Browsing opportunities at the platform level requires an institution
 * context (per-institution route `/institutions/:id/volunteer-opportunities`),
 * so the index here surfaces the caller's history and points at the
 * Institutions lab for actually finding new opportunities to sign up to.
 */
export default function VolunteersLab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["my-volunteer-history"],
    queryFn: () => getMyVolunteerHistory({ limit: 30 }),
  });

  async function handleWithdraw(opportunityId: string) {
    await withdrawSignup(opportunityId);
    qc.invalidateQueries({ queryKey: ["my-volunteer-history"] });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Volunteers"
        subtitle="Your volunteer signups + history. Browse opportunities inside the Institutions lab."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Volunteers" },
        ]}
      />

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">My history</h2>
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : !data || data.data.items.length === 0 ? (
          <p className="text-sm text-gray-500">
            You haven&apos;t signed up to any opportunities yet. Open the{" "}
            <a href="/lab/institutions" className="text-primary-600 underline">
              Institutions lab
            </a>{" "}
            to find one.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {data.data.items.map((signup) => (
              <SignupRow
                key={signup.id}
                signup={signup}
                onWithdraw={handleWithdraw}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function SignupRow({
  signup,
  onWithdraw,
}: {
  signup: VolunteerSignup;
  onWithdraw: (opportunityId: string) => Promise<void>;
}) {
  const canWithdraw =
    signup.status === "applied" || signup.status === "accepted";
  const statusVariant: Record<
    VolunteerSignup["status"],
    "gray" | "blue" | "green" | "yellow" | "red"
  > = {
    applied: "yellow",
    accepted: "blue",
    completed: "green",
    rejected: "red",
    withdrawn: "gray",
  };
  return (
    <li className="py-3 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900">
            {signup.opportunity?.title ?? "Volunteer opportunity"}
          </span>
          <Badge variant={statusVariant[signup.status]}>{signup.status}</Badge>
          {signup.opportunity?.category && (
            <Badge variant="gray">{signup.opportunity.category}</Badge>
          )}
        </div>
        <div className="text-xs text-gray-500 mt-0.5">
          {signup.opportunity?.institution?.name}
          {signup.opportunity?.date &&
            ` · ${new Date(signup.opportunity.date).toLocaleDateString()}`}
          {" · "}signed up {new Date(signup.createdAt).toLocaleDateString()}
        </div>
        {signup.rejectionReason && (
          <p className="text-xs text-red-700 mt-1">
            Reason: {signup.rejectionReason}
          </p>
        )}
      </div>
      {canWithdraw && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onWithdraw(signup.opportunityId)}
        >
          Withdraw
        </Button>
      )}
    </li>
  );
}
